import { AfterViewInit, Directive, ElementRef, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { UserLimit, UserLimitKind, UserSubscription, WorkflowType } from '@ca/core/models';
import { Message, MessagingService, UserService } from '@ca/core/services';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, take, tap } from 'rxjs/operators';
import { getNewDate } from '../../../utils/date/date-utils';
import { LIMITS_TOPIC } from '../../shared-modules/notifications-shared/services/notifications.service';

@UntilDestroy()
@Directive({ selector: '[caLimitValidator]' })
export class LimitsValidatorDirective implements OnInit, AfterViewInit {
    @Input() tooltip: NgbTooltip;
    @Input() workflowType: WorkflowType;
    @Output() next: EventEmitter<void> = new EventEmitter<void>();
    @Input() set isDisabled(disabled: boolean) {
        this.disabled = disabled;
        this.shouldShowTooltip = this.el.nativeElement.disabled = this.hasReached() || this.disabled;
    }

    disabled: boolean;
    shouldShowTooltip: boolean = false;
    limits: Map<UserLimitKind, boolean> = new Map<UserLimitKind, boolean>();
    isTrial: boolean = false;

    @HostListener('click', ['$event'])
    onClick(event: Event): void {
        event.stopPropagation();
        this.next.emit();
    }

    constructor(
        private el: ElementRef,
        private messagingService: MessagingService,
        private translateService: TranslateService,
        private userService: UserService,
    ) {}

    hasReached(): boolean | undefined {
        return (
            this.limits.get('SubscriptionEndDate') ||
            this.limits.get('WorkflowExecutionTimeInSeconds') ||
            this.limits.get('StorageInBytes') ||
            (this.workflowType === WorkflowType.APPLICATION && this.limits.get('ApplicationsParallelExecutionLimit')) ||
            (this.workflowType === WorkflowType.SEGMENTATION_TRAINING && this.limits.get('TrainingsParallelExecutionLimit')) ||
            (this.workflowType === WorkflowType.SEGMENTATION && this.limits.get('SegmentationsParallelExecutionLimit'))
        );
    }

    openTooltip(): void {
        if (this.limits.get('SubscriptionEndDate')) {
            const messageKey = this.isTrial ? 'NOTIFICATIONS.TRIAL_PERIOD_ENDED' : 'NOTIFICATIONS.SUBSCRIPTION_PERIOD_ENDED';
            this.tooltip.open({ msg: this.translateService.instant(messageKey) });
        } else if (this.limits.get('WorkflowExecutionTimeInSeconds')) {
            this.tooltip.open({ msg: this.translateService.instant('NOTIFICATIONS.COMPUTATIONAL_TIME_EXCEEDED') });
        } else if (this.limits.get('StorageInBytes')) {
            this.tooltip.open({ msg: this.translateService.instant('NOTIFICATIONS.STORAGE_EXCEEDED') });
        } else if (this.workflowType === WorkflowType.APPLICATION && this.limits.get('ApplicationsParallelExecutionLimit')) {
            this.tooltip.open({ msg: this.translateService.instant('NOTIFICATIONS.APPLICATIONS_PARALLEL_EXECUTION_REACHED') });
        } else if (this.workflowType === WorkflowType.SEGMENTATION_TRAINING && this.limits.get('TrainingsParallelExecutionLimit')) {
            this.tooltip.open({ msg: this.translateService.instant('NOTIFICATIONS.TRAININGS_PARALLEL_EXECUTION_REACHED') });
        } else if (this.workflowType === WorkflowType.SEGMENTATION && this.limits.get('SegmentationsParallelExecutionLimit')) {
            this.tooltip.open({ msg: this.translateService.instant('NOTIFICATIONS.SEGMENTATIONS_PARALLEL_EXECUTION_REACHED') });
        }
    }

    closeTooltip(): void {
        if (this.tooltip.isOpen()) {
            this.tooltip.close();
        }
    }

    ngOnInit(): void {
        this.userService.userSubscription$
            .pipe(
                take(1),
                tap((userSubscription: UserSubscription): void => {
                    const isTrial: boolean = userSubscription.name.toLowerCase().includes('trial');
                    this.handleSubscriptionType(isTrial);
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.messagingService
            .subscription(LIMITS_TOPIC)
            .pipe(
                filter((msg: Message<Map<UserLimitKind, UserLimit | string>>): boolean => msg !== null),
                filter((msg: Message<Map<UserLimitKind, UserLimit>>): boolean => msg.payload != null && typeof msg.payload[Symbol.iterator] === 'function'),
                map((msg: Message<Map<UserLimitKind, UserLimit | string>>) => msg.payload),
                map((limits: Map<UserLimitKind, UserLimit | string>) => {
                    return [...limits.entries()].reduce((acc: Map<UserLimitKind, boolean>, [key, value]: [UserLimitKind, UserLimit | string]) => {
                        return key === 'SubscriptionEndDate'
                            ? acc.set(
                                  key,
                                  getNewDate()
                                      .startOf('day')
                                      .isAfter(getNewDate(value as string).startOf('day')),
                              )
                            : acc.set(key, (value as UserLimit).usage >= (value as UserLimit).limit);
                    }, new Map());
                }),
                tap(limits => {
                    this.limits = limits;
                    this.shouldShowTooltip = this.el.nativeElement.disabled = this.hasReached() || this.disabled;
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    handleSubscriptionType(isTrial: boolean): void {
        this.isTrial = isTrial;
    }

    ngAfterViewInit(): void {
        const element = this.el.nativeElement;
        const wrapper = document.createElement('div');
        wrapper.style.display = 'flex';
        wrapper.onmouseenter = (): false | void => this.shouldShowTooltip && this.openTooltip();
        wrapper.onmouseleave = (): void => this.closeTooltip();
        element.parentNode.insertBefore(wrapper, element);
        wrapper.appendChild(element);
    }
}
